class NailBundleBar extends HTMLElement {
  constructor() {
    super();
    this.bundleItems = new Map();
    this.isMemberMode = true;
    this.membershipVariantId = null;
    this.membershipSellingPlanId = null;
    this.promotionEnabled = this.dataset.promotionEnabled === 'true';
    this.init();
  }

  init() {
    this.attachEventListeners();
    this.updateSummary();
    this.hideBundleBar(); // Start hidden, show when items are added
    this.initMemberState();
    this.checkOverflow();
  }
  
  checkOverflow() {
    const itemsWrapper = this.querySelector('.nail-bundle-bar__items-wrapper');
    const items = this.querySelector('.nail-bundle-bar__items');
    
    if (itemsWrapper && items) {
      if (items.scrollWidth > itemsWrapper.clientWidth) {
        itemsWrapper.classList.add('has-overflow');
      } else {
        itemsWrapper.classList.remove('has-overflow');
      }
    }
  }

  initMemberState() {
    const savedState = sessionStorage.getItem('booClubWidgetSelected');
    this.isMemberMode = savedState !== '0';
    
    const widget = document.querySelector('.boo-club-pdp-widget');
    if (widget) {
      this.membershipVariantId = widget.dataset.variantId;
      this.membershipSellingPlanId = widget.dataset.sellingPlanId;
    }
  }

  attachEventListeners() {
    document.addEventListener('nail:add', (event) => {
      this.addNailToBundle(event.detail);
    });

    document.addEventListener('nail:update', (event) => {
      this.updateNailInBundle(event.detail);
    });

    document.addEventListener('member:toggle', (event) => {
      this.isMemberMode = event.detail.isSelected;
      this.membershipVariantId = event.detail.variantId || this.membershipVariantId;
      this.membershipSellingPlanId = event.detail.sellingPlanId || this.membershipSellingPlanId;
      
      this.recalculatePrices();
      this.updateSummary();
    });

    this.addEventListener('click', (event) => {
      if (event.target.closest('.nail-bundle-bar__slot-remove')) {
        const slot = event.target.closest('.nail-bundle-bar__slot');
        const slotNumber = parseInt(slot.dataset.slot);
        this.removeFromSlot(slotNumber);
      }
      
      const scrollBtn = event.target.closest('.nail-bundle-bar__scroll-btn');
      if (scrollBtn) {
        const direction = scrollBtn.dataset.scroll;
        this.scrollItems(direction);
      }
    });

    const checkoutBtn = this.querySelector('.nail-bundle-bar__checkout-btn');
    if (checkoutBtn) {
      checkoutBtn.addEventListener('click', () => this.handleCheckout());
    }
  }
  
  scrollItems(direction) {
    const items = this.querySelector('.nail-bundle-bar__items');
    if (!items) return;
    
    const scrollAmount = 120; // Scroll by ~2 slots
    const currentScroll = items.scrollLeft;
    
    if (direction === 'left') {
      items.scrollLeft = currentScroll - scrollAmount;
    } else {
      items.scrollLeft = currentScroll + scrollAmount;
    }
  }

  recalculatePrices() {
    for (const [slotNumber, item] of this.bundleItems) {
      if (this.isMemberMode && item.regularPrice) {
        item.price = item.memberPrice || (item.regularPrice * 0.5);
      } else if (!this.isMemberMode && item.regularPrice) {
        item.price = item.regularPrice;
      }
      this.bundleItems.set(slotNumber, item);
    }
  }

  addNailToBundle(nailData) {
    const nextSlot = this.getNextAvailableSlot();
    if (nextSlot) {
      const enhancedData = {
        ...nailData,
        memberPrice: nailData.memberPrice || (nailData.isMemberPrice ? nailData.price : (nailData.price * 0.5)),
        regularPrice: nailData.regularPrice || (nailData.isMemberPrice ? (nailData.price / 0.5) : nailData.price),
        isMemberPrice: nailData.isMemberPrice
      };
      this.updateSlot(nextSlot, enhancedData);
      this.showBundleBar();
      this.updateSummary();
    }
  }

  updateNailInBundle(updateData) {
    for (const [slotNumber, item] of this.bundleItems) {
      if (item.productId === updateData.productId) {
        item.quantity = updateData.quantity;
        
        const slot = this.querySelector(`[data-slot="${slotNumber}"]`);
        if (slot) {
          const quantityElement = slot.querySelector('.nail-bundle-bar__slot-quantity');
          if (quantityElement) {
            quantityElement.textContent = updateData.quantity;
          }
          
          const removeBtn = slot.querySelector('.nail-bundle-bar__slot-remove');
          if (removeBtn && updateData.quantity > 1) {
            removeBtn.style.minWidth = '20px';
          }
        }
        
        this.bundleItems.set(slotNumber, item);
        
        this.updateSummary();
        break;
      }
    }
  }

  getNextAvailableSlot() {
    const slots = this.querySelectorAll('.nail-bundle-bar__slot');
    
    for (let i = 0; i < slots.length; i++) {
      if (slots[i].classList.contains('nail-bundle-bar__slot-placeholder')) {
        return i + 1;
      }
    }
    
    const newSlotNumber = slots.length + 1;
    this.addNewSlot(newSlotNumber);
    return newSlotNumber;
  }
  
  addNewSlot(slotNumber) {
    const itemsContainer = this.querySelector('.nail-bundle-bar__items');
    if (!itemsContainer) return;
    
    const newSlot = document.createElement('div');
    newSlot.className = 'nail-bundle-bar__slot nail-bundle-bar__slot-placeholder';
    newSlot.setAttribute('data-slot', slotNumber);
    
    const freeBadgeHtml = this.promotionEnabled 
      ? '<div class="nail-bundle-bar__slot-free-badge" style="display: none;">FREE</div>' 
      : '';
    newSlot.innerHTML = `<span>${slotNumber}</span>${freeBadgeHtml}`;
    
    itemsContainer.appendChild(newSlot);
    
    itemsContainer.scrollLeft = itemsContainer.scrollWidth;
  }

  updateSlot(slotNumber, item) {
    const slot = this.querySelector(`[data-slot="${slotNumber}"]`);
    if (!slot) return;

    slot.classList.remove('nail-bundle-bar__slot-placeholder');
    
    const qty = item.quantity || 1;
    const freeBadgeHtml = this.promotionEnabled 
      ? '<div class="nail-bundle-bar__slot-free-badge" style="display: none;">FREE</div>' 
      : '';
    
    slot.innerHTML = `
      <div class="nail-bundle-bar__slot-content">
        <img src="${item.image}" alt="${item.title}" class="nail-bundle-bar__slot-image">
        <button class="nail-bundle-bar__slot-remove" data-slot="${slotNumber}">
          <span class="nail-bundle-bar__slot-quantity">${qty}</span>
          <svg width="8" height="8" viewBox="0 0 12 12" fill="none">
            <path d="M9 3L3 9M3 3L9 9" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        ${freeBadgeHtml}
      </div>
    `;

    this.bundleItems.set(slotNumber, {
      ...item,
      slotNumber: slotNumber
    });
    
    if (this.promotionEnabled) {
      this.updateFreeBadges();
    }
    this.checkOverflow();
  }

  removeFromSlot(slotNumber) {
    const slot = this.querySelector(`[data-slot="${slotNumber}"]`);
    if (!slot) return;

    const removedItem = this.bundleItems.get(slotNumber);
    this.bundleItems.delete(slotNumber);

    const totalSlots = this.querySelectorAll('.nail-bundle-bar__slot').length;
    
    if (slotNumber > 3 && totalSlots > 3) {
      slot.remove();
      this.reorganizeSlots();
    } else {
      slot.classList.add('nail-bundle-bar__slot-placeholder');
      const freeBadgeHtml = this.promotionEnabled 
        ? '<div class="nail-bundle-bar__slot-free-badge" style="display: none;">FREE</div>' 
        : '';
      slot.innerHTML = `<span>${slotNumber}</span>${freeBadgeHtml}`;
    }

    if (this.promotionEnabled) {
      this.updateFreeBadges();
    }
    this.updateSummary();
    this.checkOverflow();
    
    if (removedItem) {
      document.dispatchEvent(new CustomEvent('nail:remove', {
        detail: { productId: removedItem.productId }
      }));
    }
  }
  
  reorganizeSlots() {
    const slots = this.querySelectorAll('.nail-bundle-bar__slot');
    const newBundleItems = new Map();
    
    slots.forEach((slot, index) => {
      const oldSlotNumber = parseInt(slot.getAttribute('data-slot'));
      const newSlotNumber = index + 1;
      
      slot.setAttribute('data-slot', newSlotNumber);
      
      const removeBtn = slot.querySelector('.nail-bundle-bar__slot-remove');
      if (removeBtn) {
        removeBtn.setAttribute('data-slot', newSlotNumber);
      }
      
      if (slot.classList.contains('nail-bundle-bar__slot-placeholder')) {
        const span = slot.querySelector('span');
        if (span) span.textContent = newSlotNumber;
      }
      
      if (this.bundleItems.has(oldSlotNumber)) {
        const item = this.bundleItems.get(oldSlotNumber);
        item.slotNumber = newSlotNumber;
        newBundleItems.set(newSlotNumber, item);
      }
    });
    
    this.bundleItems = newBundleItems;
  }

  showBundleBar() {
    this.style.display = 'block';
    document.body.style.paddingBottom = '120px';
  }

  hideBundleBar() {
    this.style.display = 'none';
    document.body.style.paddingBottom = '0';
  }

  updateSummary() {
    const totalItems = this.bundleItems.size;
    
    // Build array of individual item prices (expand by quantity)
    const itemPrices = [];
    Array.from(this.bundleItems.values()).forEach(item => {
      const qty = item.quantity || 1;
      const price = this.isMemberMode 
        ? (item.memberPrice || item.price) 
        : (item.regularPrice || item.price);
      
      for (let i = 0; i < qty; i++) {
        itemPrices.push(price);
      }
    });
    
    let totalPrice = 0;
    const totalQuantity = itemPrices.length;
    
    if (this.promotionEnabled) {
      // Sort prices ascending (cheapest first) for free item calculation
      itemPrices.sort((a, b) => a - b);
      
      // Calculate number of free items (Buy 2, Get 1 FREE = every 3rd item is free)
      const freeItemCount = Math.floor(totalQuantity / 3);
      
      // Calculate total price (skip the cheapest items that are free)
      for (let i = freeItemCount; i < itemPrices.length; i++) {
        totalPrice += itemPrices[i];
      }
    } else {
      // No promotion - charge for all items
      totalPrice = itemPrices.reduce((sum, price) => sum + price, 0);
    }

    const priceElement = this.querySelector('.nail-bundle-bar__checkout-price');
    if (priceElement) {
      priceElement.textContent = `$${totalPrice.toFixed(2)}`;
    }

    const badgeElement = this.querySelector('.nail-bundle-bar__checkout-badge');
    if (badgeElement) {
      badgeElement.textContent = totalItems;
    }

    const checkoutBtn = this.querySelector('.nail-bundle-bar__checkout-btn');
    if (checkoutBtn) {
      checkoutBtn.disabled = totalItems === 0;
    }

    // Show/hide bundle bar based on items
    if (totalItems === 0) {
      this.hideBundleBar();
    } else {
      this.showBundleBar();
    }

    if (this.promotionEnabled) {
      this.updatePromotionMessage();
      this.updateFreeBadges();
    }
  }

  updatePromotionMessage() {
    let totalQuantity = 0;
    for (const [slotNumber, item] of this.bundleItems) {
      totalQuantity += item.quantity || 1;
    }
    
    const promotionText = this.querySelector('.nail-bundle-bar__promotion-text');
    
    if (promotionText) {
      if (totalQuantity === 0) {
        promotionText.textContent = 'Add items to get started';
      } else if (totalQuantity === 1) {
        promotionText.textContent = 'Add 2 more and get 1 FREE';
      } else if (totalQuantity === 2) {
        promotionText.textContent = 'Add 1 more and get 1 FREE';
      } else {
        promotionText.textContent = 'You qualify for FREE item!';
      }
    }
  }

  updateFreeBadges() {
    this.querySelectorAll('.nail-bundle-bar__slot-free-badge').forEach(badge => {
      badge.style.display = 'none';
    });
    
    let totalQuantity = 0;
    for (const [slotNumber, item] of this.bundleItems) {
      totalQuantity += item.quantity || 1;
    }
    
    if (totalQuantity >= 3) {
      let maxQuantity = 0;
      let freeSlotNumber = null;
      
      for (const [slotNumber, item] of this.bundleItems) {
        if (item.quantity > maxQuantity) {
          maxQuantity = item.quantity;
          freeSlotNumber = slotNumber;
        }
      }
      
      if (freeSlotNumber) {
        const freeSlot = this.querySelector(`[data-slot="${freeSlotNumber}"]`);
        const freeBadge = freeSlot?.querySelector('.nail-bundle-bar__slot-free-badge');
        if (freeBadge) {
          freeBadge.style.display = 'block';
        }
      }
    }
  }

  handleCheckout() {
    if (this.bundleItems.size === 0) return;

    const cartItems = Array.from(this.bundleItems.values())
      .filter(item => item.variantId)
      .map(item => ({
        id: parseInt(item.variantId),
        quantity: item.quantity || 1
      }));

    if (cartItems.length === 0) {
      console.error('No valid items to add to cart');
      return;
    }

    if (this.isMemberMode && this.membershipVariantId) {
      const membershipItem = {
        id: parseInt(this.membershipVariantId),
        quantity: 1
      };
      
      if (this.membershipSellingPlanId) {
        membershipItem.selling_plan = parseInt(this.membershipSellingPlanId);
      }
      
      cartItems.push(membershipItem);
    }

    // Build request body - only include bundle attributes if promotion is enabled
    const requestBody = { items: cartItems };
    
    if (this.promotionEnabled) {
      requestBody.attributes = {
        'bundle': 'true',
        'member_pricing': this.isMemberMode ? 'true' : 'false'
      };
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody)
    })
    .then(response => response.json())
    .then(data => {
      window.location.href = '/checkout';
    })
    .catch(error => {
      console.error('Error adding bundle to cart:', error);
    });
  }
}

customElements.define('nail-bundle-bar', NailBundleBar);
